// Phase HR-002 hardening — offline write queue.
//
// Purpose: when the backend is briefly unreachable (worker pool saturated,
// network blip, brief restart), operators on the shop floor MUST NOT lose
// the data they just entered. Their save call gets stored in IndexedDB,
// the UI shows "queued, will sync", and a background loop replays the
// requests as soon as the backend comes back. This makes the system
// resilient to short outages without changing how any page calls
// apiClient — they just call as normal.
//
// Why IndexedDB and not localStorage:
//   * Survives browser restart, tab close, even device reboot.
//   * 50+ MB capacity (vs ~5 MB for localStorage) — enough for thousands
//     of queued writes.
//   * Async, won't block the main thread when shop-floor tablet is slow.
//
// Safety constraints (REAL-TIME DATA INTEGRITY):
//   * Only methods we know are safe to retry are accepted into the queue:
//     POST, PATCH, PUT, DELETE on the explicit allow-list below.
//   * Each queued item gets a deterministic idempotency_key (UUID v4
//     generated at enqueue time) so the backend can dedupe replays if
//     it ever supports it. (Backend doesn't dedupe today; the next
//     hardening pass adds an idempotency table.)
//   * Replay attempts are serial — one at a time, in original FIFO order —
//     so a POST that depends on a prior POST's id can't get reordered.
//   * Items that fail with a permanent error (400, 404, 409, 422) are
//     marked FAILED so the operator can see them in the queue inspector
//     and act, instead of silently retrying forever.
//
// Read calls (GET) are NEVER queued — they're idempotent, the apiClient
// retry layer already handles them, and queued GETs don't help anyone.

const DB_NAME = 'erp_offline_queue'
const DB_VERSION = 1
const STORE = 'pending_writes'

// HR-004 follow-up — give up after this many retries. Stuck items
// (almost always: backend has moved on but request body has stale
// references, e.g. machine deleted, side already claimed) end up
// looping forever otherwise. After this cap they go to FAILED so the
// user can manually retry / discard. 20 ≈ 10 minutes of background
// retry attempts, which is plenty for a transient backend blip.
const MAX_REPLAY_ATTEMPTS = 20

export type QueueItemStatus = 'PENDING' | 'REPLAYING' | 'FAILED'

export interface QueueItem {
  id: string                  // uuid v4
  enqueued_at: number         // ms epoch
  method: string              // POST | PATCH | PUT | DELETE
  url: string                 // /grn/{id}/sample-bales/{baleId}
  data?: unknown              // request body
  params?: Record<string, unknown>
  idempotency_key: string     // sent as X-Idempotency-Key header
  attempts: number
  last_error?: string
  status: QueueItemStatus
}

// ── Allow-list ───────────────────────────────────────────────────────
// Only writes matching these URL patterns are eligible for queueing. The
// list is intentionally narrow — we want to be sure each queued path is
// safe to replay (no exotic side effects, no ledger postings that can't
// be reversed). To extend, add a regex here and document why.
const QUEUEABLE_URL_PATTERNS: RegExp[] = [
  // HR: attendance, todo evidence/complete/block, allocations
  /\/hr\/attendance(\b|\/)/i,
  /\/hr\/tasks\/[^/]+\/(complete|block|start|verify|upload-evidence)\b/i,
  /\/hr\/worker-allocations(\b|\/)/i,
  // GRN bale-level operations (where the storm risk lives)
  /\/grn\/[^/]+\/sample-bales\//i,
  /\/grn\/[^/]+\/bulk-hvi\b/i,
  /\/grn\/[^/]+\/bulk-moisture\b/i,
  // Punch logs / live study captures — pure append-only events
  /\/hr\/punches\b/i,
  /\/hr\/live-studies\/[^/]+\/events\b/i,
  // Ring Frame Time & Motion (Phase 1) — every rf/ call that mutates an
  // EXISTING round/event id. Deliberately EXCLUDES the two id-creating
  // calls (`rf/round/start` and `rf/round/{id}/mend/start`): those return
  // a brand-new server id that the tablet needs immediately to keep
  // capturing (e.g. mend/stop needs the event id mend/start returns), and
  // a queued create only yields a synthetic `{_queued:true}` marker with
  // no id — so those two rely on apiClient's built-in retry-with-backoff
  // for brief blips and surface a clear error if that's exhausted, rather
  // than being silently swallowed into the queue. Every other rf/ call
  // below already holds its target id locally before firing, so it is
  // always safe to queue and replay.
  /\/hr\/studies\/sessions\/[^/]+\/rf\/round\/[^/]+\/(end|counter|left-down)\b/i,
  /\/hr\/studies\/sessions\/[^/]+\/rf\/mend\/[^/]+\/(stop|retry|repeat)\b/i,
]

function isQueueable(method: string, url: string): boolean {
  const m = method.toUpperCase()
  if (m === 'GET' || m === 'HEAD' || m === 'OPTIONS') return false
  return QUEUEABLE_URL_PATTERNS.some((re) => re.test(url))
}

// ── IndexedDB plumbing ──────────────────────────────────────────────

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' })
        store.createIndex('by_enqueued', 'enqueued_at')
        store.createIndex('by_status', 'status')
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => Promise<T> | T): Promise<T> {
  const db = await openDb()
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode)
    const store = t.objectStore(STORE)
    let result: T
    Promise.resolve(fn(store))
      .then((r) => { result = r })
      .catch(reject)
    t.oncomplete = () => resolve(result)
    t.onerror = () => reject(t.error)
    t.onabort = () => reject(t.error)
  })
}

function uuidv4(): string {
  // crypto.randomUUID is widely available; fallback for very old browsers.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as Crypto & { randomUUID: () => string }).randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// ── Public API ──────────────────────────────────────────────────────

export interface EnqueueInput {
  method?: string
  url?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any
  params?: Record<string, unknown>
}

/**
 * Called by apiClient's response interceptor after retry exhaustion.
 * Returns the queued item if accepted, null if the request isn't safe
 * to queue (e.g. GET, non-allowlisted URL, or a permanent 4xx error —
 * let those fail through to the caller).
 */
export async function tryEnqueueOnFailure(
  cfg: EnqueueInput,
  err: unknown,
): Promise<QueueItem | null> {
  const method = (cfg.method ?? 'GET').toUpperCase()
  const url = cfg.url ?? ''
  if (!isQueueable(method, url)) return null

  // CRITICAL: never queue permanent errors. A 400/404/409/422 means the
  // request body is wrong (missing field, validation failed, conflict
  // with existing data) — retrying won't help. Queueing such requests
  // and synthesizing a 202 makes the UI lie ("Allocated 0 sides")
  // instead of showing the real error to the user. Only 5xx, network
  // errors, and timeouts are transient and worth queueing.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const status: number | undefined = (err as any)?.response?.status
  if (status !== undefined && status >= 400 && status < 500) {
    return null
  }

  const item: QueueItem = {
    id: uuidv4(),
    enqueued_at: Date.now(),
    method, url,
    data: cfg.data,
    params: cfg.params,
    idempotency_key: uuidv4(),
    attempts: 0,
    status: 'PENDING',
  }
  await tx('readwrite', (store) => new Promise<void>((resolve, reject) => {
    const r = store.add(item)
    r.onsuccess = () => resolve()
    r.onerror = () => reject(r.error)
  }))
  notifyQueueChanged()
  // Kick the replay loop — best-effort, may fail if offline.
  setTimeout(() => { void replayOnce() }, 250)
  return item
}

export async function listQueue(): Promise<QueueItem[]> {
  return tx('readonly', (store) => new Promise<QueueItem[]>((resolve, reject) => {
    const r = store.index('by_enqueued').getAll()
    r.onsuccess = () => resolve(r.result as QueueItem[])
    r.onerror = () => reject(r.error)
  }))
}

export async function countQueue(): Promise<{ pending: number; failed: number }> {
  const items = await listQueue()
  return {
    pending: items.filter((i) => i.status === 'PENDING' || i.status === 'REPLAYING').length,
    failed: items.filter((i) => i.status === 'FAILED').length,
  }
}

export async function deleteItem(id: string): Promise<void> {
  await tx('readwrite', (store) => new Promise<void>((resolve, reject) => {
    const r = store.delete(id)
    r.onsuccess = () => resolve()
    r.onerror = () => reject(r.error)
  }))
  notifyQueueChanged()
}

export async function retryItem(id: string): Promise<void> {
  await tx('readwrite', (store) => new Promise<void>((resolve, reject) => {
    const r = store.get(id)
    r.onsuccess = () => {
      const item = r.result as QueueItem | undefined
      if (!item) { resolve(); return }
      item.status = 'PENDING'
      item.last_error = undefined
      const u = store.put(item)
      u.onsuccess = () => resolve()
      u.onerror = () => reject(u.error)
    }
    r.onerror = () => reject(r.error)
  }))
  notifyQueueChanged()
  void replayOnce()
}

// ── Background replay loop ──────────────────────────────────────────

let replaying = false

/**
 * Drains the queue: walks PENDING items in FIFO order, posts each
 * via a bare axios call (NOT apiClient — we don't want the retry
 * interceptor to re-queue an already-queued item). Stops at the first
 * network error so we don't burn through the queue while offline.
 */
export async function replayOnce(): Promise<void> {
  if (replaying) return
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return
  replaying = true
  try {
    // Import lazily — apiClient -> offlineQueue -> apiClient would cycle.
    const { default: axios } = await import('axios')
    const items = await listQueue()
    const pending = items
      .filter((i) => i.status === 'PENDING')
      .sort((a, b) => a.enqueued_at - b.enqueued_at)

    // Re-read auth token at replay time so a re-login is picked up.
    const raw = localStorage.getItem('auth-storage')
    let token: string | undefined
    try {
      const parsed = raw ? JSON.parse(raw) : null
      token = parsed?.state?.accessToken
    } catch { /* ignore */ }

    for (const item of pending) {
      try {
        await markStatus(item.id, 'REPLAYING')
        await axios.request({
          baseURL: '/api/v1',
          method: item.method,
          url: item.url,
          data: item.data,
          params: item.params,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'X-Idempotency-Key': item.idempotency_key,
          },
          timeout: 30_000,
        })
        // Success — remove from queue.
        await deleteItem(item.id)
      } catch (e) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const err = e as any
        const status: number | undefined = err?.response?.status
        // Permanent errors → FAILED. Transient → leave PENDING and stop.
        if (status === 400 || status === 404 || status === 409
            || status === 422 || status === 403 || status === 401) {
          await markStatus(item.id, 'FAILED', `${status}: ${err?.response?.data?.detail ?? err.message}`)
          // Keep walking — these failures don't affect later items.
          continue
        }
        // 5xx / network — give up after MAX_REPLAY_ATTEMPTS so a
        // permanently-stuck item doesn't loop forever. The attempts
        // counter has just been incremented by markStatus('REPLAYING').
        if (item.attempts + 1 >= MAX_REPLAY_ATTEMPTS) {
          await markStatus(
            item.id, 'FAILED',
            `Gave up after ${MAX_REPLAY_ATTEMPTS} attempts. ` +
            `Last error: ${err?.message ?? 'unknown'}. ` +
            `Request body may reference data that no longer exists ` +
            `(e.g. deleted machine, side already taken). Discard or retry manually.`,
          )
          continue
        }
        // Transient — leave PENDING and stop the loop so later replays
        // preserve FIFO order.
        await markStatus(item.id, 'PENDING', err?.message ?? 'replay failed')
        break
      }
    }
  } finally {
    replaying = false
    notifyQueueChanged()
  }
}

async function markStatus(id: string, status: QueueItemStatus, last_error?: string) {
  await tx('readwrite', (store) => new Promise<void>((resolve, reject) => {
    const r = store.get(id)
    r.onsuccess = () => {
      const item = r.result as QueueItem | undefined
      if (!item) { resolve(); return }
      item.status = status
      item.attempts += status === 'REPLAYING' ? 1 : 0
      item.last_error = last_error
      const u = store.put(item)
      u.onsuccess = () => resolve()
      u.onerror = () => reject(u.error)
    }
    r.onerror = () => reject(r.error)
  }))
}

// ── Reactive notification ───────────────────────────────────────────

type Listener = () => void
const listeners = new Set<Listener>()

export function onQueueChanged(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function notifyQueueChanged() {
  for (const l of listeners) {
    try { l() } catch { /* ignore */ }
  }
}

// ── Boot ────────────────────────────────────────────────────────────

/** Wire up online / focus events to kick replay. Idempotent. */
export function initOfflineQueue() {
  if (typeof window === 'undefined') return
  if ((window as Window & { __erp_queue_inited?: boolean }).__erp_queue_inited) return
  ;(window as Window & { __erp_queue_inited?: boolean }).__erp_queue_inited = true

  window.addEventListener('online', () => { void replayOnce() })
  window.addEventListener('focus', () => { void replayOnce() })
  // Periodic best-effort replay — every 30s. Cheap; reads queue head only.
  setInterval(() => { void replayOnce() }, 30_000)
  // Initial kick: try to drain any items left over from a previous session.
  setTimeout(() => { void replayOnce() }, 1500)
}
